import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../auth/contexts/AuthContext';
import {
  listMembers,
  inviteMember,
  setMemberRole,
  disableMember,
  enableMember,
  revokeInvite,
} from '../utils/membersApi';

const formatDate = (value) => {
  if (!value) return '–';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '–' : date.toLocaleDateString('nb-NO', { dateStyle: 'medium' });
};

const STATUS_LABELS = { active: 'Active', invited: 'Invited', disabled: 'Disabled' };
const STATUS_STYLES = {
  active: 'bg-gray-100 text-gray-700',
  invited: 'border border-gray-200 text-gray-500',
  disabled: 'bg-red-50 text-red-700',
};

const actionButtonClass =
  'px-2.5 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:text-gray-300 disabled:hover:bg-white';

const MembersPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [inviteError, setInviteError] = useState(null);
  const [inviting, setInviting] = useState(false);

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    setError(null);
    try {
      setMembers(await listMembers());
    } catch (err) {
      setError(`Failed to load members: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (member, action, successMessage) => {
    setError(null); setSuccess(null);
    setBusyId(member.id);
    try {
      await action();
      setSuccess(successMessage);
      await loadMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleRoleChange = (member) => {
    const role = member.role === 'admin' ? 'member' : 'admin';
    const question = role === 'admin'
      ? `Give ${member.email} admin access? Admins can invite and manage all members.`
      : `Remove admin access from ${member.email}?`;
    if (!window.confirm(question)) return;
    runAction(member, () => setMemberRole(member.id, role), `${member.email} is now ${role === 'admin' ? 'an admin' : 'a member'}.`);
  };

  const handleDisable = (member) => {
    if (!window.confirm(`Disable ${member.email}? They won't be able to sign in until you enable them again.`)) return;
    runAction(member, () => disableMember(member.id), `${member.email} has been disabled.`);
  };

  const handleEnable = (member) =>
    runAction(member, () => enableMember(member.id), `${member.email} can sign in again.`);

  const handleResend = (member) =>
    runAction(member, () => inviteMember(member.email, member.role), `Invite sent again to ${member.email}.`);

  const handleRevoke = (member) => {
    if (!window.confirm(`Revoke the invite for ${member.email}? The invite link will stop working.`)) return;
    runAction(member, () => revokeInvite(member.id), `Invite for ${member.email} revoked.`);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError(null); setError(null); setSuccess(null);
    setInviting(true);
    try {
      await inviteMember(inviteForm.email, inviteForm.role);
      setSuccess(`Invite sent to ${inviteForm.email}.`);
      closeInviteModal();
      await loadMembers();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteForm({ email: '', role: 'member' });
    setInviteError(null);
  };

  const rowActions = (member) => {
    if (member.id === user?.id) {
      return <span className="text-xs text-gray-400">You</span>;
    }
    const busy = busyId === member.id;
    return (
      <div className="flex justify-end gap-1.5">
        {member.status === 'invited' ? (
          <>
            <button onClick={() => handleResend(member)} disabled={busy} className={actionButtonClass}>Resend invite</button>
            <button onClick={() => handleRevoke(member)} disabled={busy} className={actionButtonClass}>Revoke</button>
          </>
        ) : (
          <button onClick={() => handleRoleChange(member)} disabled={busy} className={actionButtonClass}>
            {member.role === 'admin' ? 'Make member' : 'Make admin'}
          </button>
        )}
        {member.status === 'active' && (
          <button onClick={() => handleDisable(member)} disabled={busy} className={actionButtonClass}>Disable</button>
        )}
        {member.status === 'disabled' && (
          <button onClick={() => handleEnable(member)} disabled={busy} className={actionButtonClass}>Enable</button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Loading members...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-xl font-semibold text-black">Members</h1>
          <p className="mt-1 text-sm text-gray-400">Invite people and manage who has admin access.</p>
        </header>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
        {success && <p className="text-sm text-gray-600 mb-4">{success}</p>}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-black">Members ({members.length})</h2>
          <button onClick={() => setShowInviteModal(true)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
            Invite member
          </button>
        </div>

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Email</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Role</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Status</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Last sign-in</th>
                  <th className="py-2.5 px-4"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2.5 px-4 font-medium text-black">{member.email}</td>
                    <td className="py-2.5 px-4 text-gray-500">{member.role === 'admin' ? 'Admin' : 'Member'}</td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_STYLES[member.status]}`}>
                        {STATUS_LABELS[member.status]}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-gray-500">
                      {member.status === 'invited' ? `Invited ${formatDate(member.invited_at)}` : formatDate(member.last_sign_in_at)}
                    </td>
                    <td className="py-2.5 px-4 text-right whitespace-nowrap">{rowActions(member)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {members.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              No members found.
            </div>
          )}
        </div>

        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeInviteModal}>
            <div className="w-full max-w-md bg-white border border-gray-200 rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-black">Invite member</h2>
                <button onClick={closeInviteModal} className="text-gray-400 hover:text-black transition-colors" aria-label="Close">&times;</button>
              </div>
              <form onSubmit={handleInvite}>
                <div className="px-5 py-4 space-y-3">
                  <div>
                    <label htmlFor="inviteEmail" className="block text-xs font-medium text-gray-500 mb-1">Email *</label>
                    <input
                      type="email" id="inviteEmail" value={inviteForm.email} required disabled={inviting}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="name@example.com"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm placeholder-gray-300 focus:outline-none focus:border-black transition-colors disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="inviteRole" className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                    <select
                      id="inviteRole" value={inviteForm.role} disabled={inviting}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-black transition-colors disabled:bg-gray-50"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <p className="mt-1.5 text-xs text-gray-400">Admins can also invite and manage members.</p>
                  </div>
                  {inviteError && <p className="text-sm text-red-600">{inviteError}</p>}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                  <button type="button" onClick={closeInviteModal} className="px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" disabled={inviting} className="px-3 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-900 transition-colors disabled:bg-gray-300">
                    {inviting ? 'Sending...' : 'Send invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersPage;
